<script setup lang="ts">
/**
 * Step 2 du checkout événement :
 * cartes participants avec identité + custom fields + options
 * (checkbox ou stepper selon max_per_ticket).
 */
import { useErreursEffaceesALaSaisie } from "~/composables/useErreursEffaceesALaSaisie";
import { MESSAGE_NOM_EN_DOUBLE, concerneTarif, indicesNomsEnDouble } from "@donoor/utils";
import type { TicketOption } from "~/types/event";
import { isValidEmailStrict } from "@donoor/validators";
import { allerAuPremierChampEnErreur, validerChamps } from "~/utils/validationChamps";
import { usePaysAssociation } from "~/composables/usePaysAssociation";
import { useUrlEnvoiDocument } from "~/composables/useEnvoiDocumentFormulaire";
import { BORDURE_CHAMP_PUBLIC, BORDURE_CHAMP_PUBLIC_ERREUR } from "~/utils/stylesChamps";

const { t } = useI18n();
const { formatCurrency } = useFormatLangue();

// Pays de l'association : un téléphone tapé sans indicatif s'y lit.
const paysAsso = usePaysAssociation();
// Documents demandés au formulaire (certificat médical…) : rattachés à cet événement.
const urlEnvoiDocument = useUrlEnvoiDocument("events");

interface AttendeeLocal {
  firstName: string;
  lastName: string;
  email: string;
  custom_data: Record<string, unknown>;
  ticketTypeId: string;
  ticketTypeName: string;
  selectedOptions: Record<string, number>;
}

const props = defineProps<{
  attendees: AttendeeLocal[];
  payerIndex: number | null;
  eventCustomFields: unknown[];
  getOptionsForTicketType: (id: string) => TicketOption[];
  allAttendeesComplete: boolean;
}>();

const emit = defineEmits<{
  (e: 'toggle-payer', idx: number): void;
  (e: 'back' | 'continue'): void;
}>();

// ── Validation à la demande ────────────────────────────────────────────────
//
// 🔴 Le bouton « Continuer » était DÉSACTIVÉ tant que le formulaire était
// incomplet : le clic ne produisait rien et rien ne disait POURQUOI (Karim
// 2026-09-21). Il reste actif, et c'est le clic qui valide, cerne de rouge le
// champ fautif, explique la raison, et défile jusqu'à lui.
//
// Règles STRICTEMENT identiques à l'adhésion (`validerChamps`) : les deux
// parcours divergeaient au premier ajustement s'ils validaient chacun de leur
// côté.

const erreursIdentite = ref<Record<number, Record<string, string>>>({});
const erreursChamps = ref<Record<number, Record<string, string>>>({});
const aTenteDeContinuer = ref(false);


function prefixeParticipant(idx: number): string {
  return `a${idx}`;
}

function validerIdentite(a: AttendeeLocal): Record<string, string> {
  const e: Record<string, string> = {};
  if (!a.firstName?.trim()) e.firstName = t("billetterie.partage.erreurPrenomObligatoire");
  if (!a.lastName?.trim()) e.lastName = t("billetterie.partage.erreurNomObligatoire");
  if (!a.email?.trim()) {
    e.email = t("billetterie.partage.erreurEmailObligatoire");
  } else if (!isValidEmailStrict(a.email)) {
    e.email = t("billetterie.partage.erreurEmailInvalideExemple");
  }
  return e;
}

/**
 * Questions posées pour un billet : celles qui concernent son TARIF (tarifs
 * concernés, Karim 2026-10-06) — les autres ne s'affichent ni ne bloquent.
 */
function questionsDuBillet(ticketTypeId: string): unknown[] {
  return (props.eventCustomFields as Array<{ applies_to?: string; applies_to_ids?: string[] }>).filter((f) =>
    concerneTarif(f, ticketTypeId),
  );
}

function auClicContinuer() {
  aTenteDeContinuer.value = true;
  const parIdentite: Record<number, Record<string, string>> = {};
  const parChamps: Record<number, Record<string, string>> = {};

  props.attendees.forEach((a, idx) => {
    const eIdentite = validerIdentite(a);
    if (Object.keys(eIdentite).length > 0) parIdentite[idx] = eIdentite;
    // Seulement les questions de SON billet (tarifs concernés).
    const champs = (questionsDuBillet(a.ticketTypeId) as Array<{
      key: string;
      label: string;
      type: string;
      required?: boolean;
    }>).map((f) => ({
      id: f.key,
      label: f.label,
      type: f.type as TypeChampPublic,
      requis: !!f.required,
    }));
    const eChamps = validerChamps(champs, a.custom_data, paysAsso.value);
    if (Object.keys(eChamps).length > 0) parChamps[idx] = eChamps;
  });

  // Un nom différent sur chaque billet (Karim 2026-10-09) — l'API refuse
  // aussi. L'email, lui, peut se répéter (un parent pour ses enfants).
  for (const idx of indicesNomsEnDouble(props.attendees.map((a) => `${a.firstName} ${a.lastName}`))) {
    const e = (parIdentite[idx] ??= {});
    if (!e.firstName && !e.lastName) e.lastName = MESSAGE_NOM_EN_DOUBLE;
  }

  erreursIdentite.value = parIdentite;
  erreursChamps.value = parChamps;

  for (let idx = 0; idx < props.attendees.length; idx++) {
    const eId = parIdentite[idx];
    if (eId && Object.keys(eId).length > 0) {
      allerAuPremierChampEnErreur(eId, prefixeParticipant(idx));
      return;
    }
    const eCh = parChamps[idx];
    if (eCh && Object.keys(eCh).length > 0) {
      allerAuPremierChampEnErreur(eCh, prefixeParticipant(idx));
      return;
    }
  }

  emit("continue");
}

// Identité à plat (« 0.firstName ») : une erreur s'efface dès qu'on
// modifie le champ, participant par participant (cf. composable).
const identiteEffacable = useErreursEffaceesALaSaisie(
  () => {
    const plat: Record<string, string> = {};
    for (const [idx, e] of Object.entries(erreursIdentite.value)) {
      for (const [champ, msg] of Object.entries(e)) plat[`${idx}.${champ}`] = msg;
    }
    return plat;
  },
  () => {
    const plat: Record<string, unknown> = {};
    props.attendees.forEach((p, idx) => {
      for (const champ of ["firstName", "lastName", "email"]) {
        plat[`${idx}.${champ}`] = (p as unknown as Record<string, unknown>)[champ];
      }
    });
    return plat;
  },
);

function erreurIdentite(idx: number, champ: string): string | undefined {
  if (!aTenteDeContinuer.value) return undefined;
  return identiteEffacable.erreurDe(`${idx}.${champ}`);
}

function classesIdentite(idx: number, champ: string): string {
  const base =
    "w-full px-4 py-3 rounded-xl bg-white dark:bg-[#0f0f0f] text-sm text-[#2d2e5c] dark:text-white placeholder-gray-400 transition-colors";
  return `${base} ${erreurIdentite(idx, champ) ? BORDURE_CHAMP_PUBLIC_ERREUR : BORDURE_CHAMP_PUBLIC}`;
}
</script>

<template>
  <div class="p-3 md:p-5">
    <div class="flex items-center gap-2 mb-5 pb-3 border-b border-gray-100 dark:border-gray-800">
      <UIcon name="i-lucide-info" class="size-4 text-[#717198]" />
      <i18n-t keypath="billetterie.partage.champsObligatoiresEtoile" tag="p" class="text-xs text-[#717198] dark:text-gray-500">
        <template #etoile>
          <span class="text-red-500">*</span>
        </template>
      </i18n-t>
    </div>

    <div class="space-y-4">
      <div
        v-for="(attendee, idx) in attendees"
        :key="idx"
        class="rounded-2xl border border-gray-200 dark:border-[#262626] overflow-hidden"
      >
        <div class="px-5 py-3 bg-gray-50 dark:bg-[#0f0f0f] border-b border-gray-100 dark:border-[#1e1e1e]">
          <div class="flex items-center gap-3">
            <div class="w-7 h-7 rounded-full flex items-center justify-center shrink-0" :style="{ background: 'var(--accent, #212121)' }">
              <span class="text-xs font-bold" :style="{ color: 'var(--accent-text, #fff)' }">{{ idx + 1 }}</span>
            </div>
            <p class="text-sm font-semibold text-[#2d2e5c] dark:text-white">{{ t('billetterie.bslugStepAttendees.titreParticipant', { ticketTypeName: attendee.ticketTypeName, numero: idx + 1 }) }}</p>
          </div>
          <div class="flex items-center justify-between mt-3 px-3 py-2.5 rounded-xl bg-white dark:bg-[#141414] border border-gray-200 dark:border-[#262626]">
            <div class="flex items-center gap-2">
              <UIcon name="i-lucide-credit-card" class="size-4 text-[#505870] dark:text-gray-400" />
              <span class="text-sm font-medium text-[#2d2e5c] dark:text-gray-300">{{ t('billetterie.bslugStepAttendees.participantPaiement') }}</span>
            </div>
            <div class="accent-switch">
              <USwitch :model-value="payerIndex === idx" size="xs" @update:model-value="$emit('toggle-payer', idx)" />
            </div>
          </div>
        </div>

        <div class="p-5 space-y-4">
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label :for="`${prefixeParticipant(idx)}-firstName`" class="block text-sm font-medium text-[#2d2e5c] dark:text-gray-300 mb-1.5">{{ t('billetterie.bslugStepAttendees.labelPrenom') }} <span class="text-red-500">*</span></label>
              <input :id="`${prefixeParticipant(idx)}-firstName`" v-model="attendee.firstName" type="text" autocomplete="given-name" :placeholder="t('billetterie.bslugStepAttendees.labelPrenom')" :aria-invalid="!!erreurIdentite(idx, 'firstName') || undefined" :class="classesIdentite(idx, 'firstName')" />
              <p v-if="erreurIdentite(idx, 'firstName')" class="flex items-start gap-1.5 text-xs text-red-500 mt-1.5">
                <UIcon name="i-lucide-alert-circle" class="size-3.5 shrink-0 mt-px" /><span>{{ erreurIdentite(idx, 'firstName') }}</span>
              </p>
            </div>
            <div>
              <label :for="`${prefixeParticipant(idx)}-lastName`" class="block text-sm font-medium text-[#2d2e5c] dark:text-gray-300 mb-1.5">{{ t('billetterie.bslugStepAttendees.labelNom') }} <span class="text-red-500">*</span></label>
              <input :id="`${prefixeParticipant(idx)}-lastName`" v-model="attendee.lastName" type="text" autocomplete="family-name" :placeholder="t('billetterie.bslugStepAttendees.labelNom')" :aria-invalid="!!erreurIdentite(idx, 'lastName') || undefined" :class="classesIdentite(idx, 'lastName')" />
              <p v-if="erreurIdentite(idx, 'lastName')" class="flex items-start gap-1.5 text-xs text-red-500 mt-1.5">
                <UIcon name="i-lucide-alert-circle" class="size-3.5 shrink-0 mt-px" /><span>{{ erreurIdentite(idx, 'lastName') }}</span>
              </p>
            </div>
          </div>
          <div>
            <label :for="`${prefixeParticipant(idx)}-email`" class="block text-sm font-medium text-[#2d2e5c] dark:text-gray-300 mb-1.5">{{ t('billetterie.bslugStepAttendees.labelEmail') }} <span class="text-red-500">*</span></label>
            <input :id="`${prefixeParticipant(idx)}-email`" v-model="attendee.email" type="email" inputmode="email" autocomplete="email" :placeholder="t('billetterie.bslugStepAttendees.placeholderEmailExemple')" :aria-invalid="!!erreurIdentite(idx, 'email') || undefined" :class="classesIdentite(idx, 'email')" />
            <p v-if="erreurIdentite(idx, 'email')" class="flex items-start gap-1.5 text-xs text-red-500 mt-1.5">
              <UIcon name="i-lucide-alert-circle" class="size-3.5 shrink-0 mt-px" /><span>{{ erreurIdentite(idx, 'email') }}</span>
            </p>
            <p v-else class="text-[11px] text-[#717198] dark:text-gray-500 mt-1.5">{{ t('billetterie.bslugStepAttendees.billetEnvoyeAdresse') }}</p>
          </div>

          <CustomFieldsForm
            v-if="questionsDuBillet(attendee.ticketTypeId).length > 0"
            v-model="attendee.custom_data"
            :fields="(questionsDuBillet(attendee.ticketTypeId) as any)"
            :erreurs="aTenteDeContinuer ? erreursChamps[idx] : undefined"
            :prefixe-id="prefixeParticipant(idx)"
            :url-envoi-document="urlEnvoiDocument"
          />

          <div v-if="getOptionsForTicketType(attendee.ticketTypeId).length > 0">
            <p class="text-xs font-semibold text-[#505870] dark:text-gray-400 uppercase tracking-wide mb-2">{{ t('billetterie.partage.options') }}</p>
            <div class="space-y-2">
              <div
                v-for="opt in getOptionsForTicketType(attendee.ticketTypeId)"
                :key="opt.id"
                class="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl border transition-all"
                :class="(attendee.selectedOptions[opt.id] || 0) > 0
                  ? 'border-gray-200 dark:border-[#262626] bg-[#f5f5f5] dark:bg-[#1a1a1a]'
                  : 'border-gray-200 dark:border-[#262626]'"
              >
                <template v-if="!opt.max_per_ticket || opt.max_per_ticket <= 1">
                  <button
                    type="button"
                    class="w-full flex items-center gap-3 text-left"
                    :disabled="!((attendee.selectedOptions[opt.id] || 0) > 0) && !!opt.max_quantity && (opt.max_quantity - (opt.used_count || 0)) <= 0"
                    @click="attendee.selectedOptions[opt.id] = (attendee.selectedOptions[opt.id] || 0) > 0 ? 0 : 1"
                  >
                    <div
                      class="w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 transition-colors"
                      :class="(attendee.selectedOptions[opt.id] || 0) > 0 ? '' : 'border-gray-300 dark:border-gray-600'"
                      :style="(attendee.selectedOptions[opt.id] || 0) > 0 ? { background: 'var(--accent, #212121)', borderColor: 'var(--accent, #212121)' } : undefined"
                    >
                      <UIcon v-if="(attendee.selectedOptions[opt.id] || 0) > 0" name="i-lucide-check" class="size-3" :style="{ color: 'var(--accent-text, #fff)' }" />
                    </div>
                    <div class="flex-1 min-w-0">
                      <p class="text-sm font-medium text-[#2d2e5c] dark:text-white">{{ opt.name }}</p>
                      <p v-if="opt.description" class="text-[11px] text-[#717198] dark:text-gray-400 mt-0.5">{{ opt.description }}</p>
                      <span v-if="opt.max_quantity && (opt.max_quantity - (opt.used_count || 0)) <= 0 && !((attendee.selectedOptions[opt.id] || 0) > 0)" class="text-[10px] font-medium text-red-500">{{ t('billetterie.partage.epuise') }}</span>
                    </div>
                    <span class="text-xs font-semibold shrink-0" :class="Number(opt.price) > 0 ? 'text-[#2d2e5c] dark:text-white' : 'text-emerald-600 dark:text-emerald-400'">
                      {{ Number(opt.price) > 0 ? `+${formatCurrency(Number(opt.price))}` : t('billetterie.partage.gratuit') }}
                    </span>
                  </button>
                </template>
                <template v-else>
                  <div class="flex-1 min-w-0">
                    <p class="text-sm font-medium text-[#2d2e5c] dark:text-white">{{ opt.name }}</p>
                    <p v-if="opt.description" class="text-[11px] text-[#717198] dark:text-gray-400 mt-0.5">{{ opt.description }}</p>
                    <div class="flex items-center gap-2 mt-0.5">
                      <span v-if="opt.max_quantity" class="text-[10px] text-[#717198] dark:text-gray-500">
                        {{ t('billetterie.bslugStepAttendees.placesRestantesOption', Math.max(0, opt.max_quantity - (opt.used_count || 0))) }}
                      </span>
                      <span class="text-[10px] text-[#717198] dark:text-gray-500">{{ t('billetterie.bslugStepAttendees.maxParBillet', { n: opt.max_per_ticket }) }}</span>
                    </div>
                  </div>
                  <div class="flex items-center gap-2 shrink-0">
                    <span class="text-xs font-semibold" :class="Number(opt.price) > 0 ? 'text-[#2d2e5c] dark:text-white' : 'text-emerald-600 dark:text-emerald-400'">
                      {{ Number(opt.price) > 0 ? `+${formatCurrency(Number(opt.price))}` : t('billetterie.partage.gratuit') }}
                    </span>
                    <div class="flex items-center gap-1.5">
                      <button
                        type="button"
                        :aria-label="t('billetterie.partage.diminuerQuantiteDe', { nom: opt.name })"
                        class="w-7 h-7 rounded-lg border border-gray-200 dark:border-[#333] flex items-center justify-center text-[#2d2e5c] dark:text-white disabled:opacity-40 transition-colors hover:bg-gray-100 dark:hover:bg-[#222]"
                        :disabled="(attendee.selectedOptions[opt.id] || 0) <= 0"
                        @click="attendee.selectedOptions[opt.id] = Math.max(0, (attendee.selectedOptions[opt.id] || 0) - 1)"
                      >
                        <UIcon name="i-lucide-minus" class="size-3.5" />
                      </button>
                      <span class="w-5 text-center text-sm font-semibold text-[#2d2e5c] dark:text-white">{{ attendee.selectedOptions[opt.id] || 0 }}</span>
                      <button
                        type="button"
                        :aria-label="t('billetterie.partage.augmenterQuantiteDe', { nom: opt.name })"
                        class="w-7 h-7 rounded-lg border border-gray-200 dark:border-[#333] flex items-center justify-center text-[#2d2e5c] dark:text-white disabled:opacity-40 transition-colors hover:bg-gray-100 dark:hover:bg-[#222]"
                        :disabled="(attendee.selectedOptions[opt.id] || 0) >= (opt.max_per_ticket ?? Infinity) || (!!opt.max_quantity && (opt.max_quantity - (opt.used_count || 0)) <= (attendee.selectedOptions[opt.id] || 0))"
                        @click="attendee.selectedOptions[opt.id] = Math.min(opt.max_per_ticket ?? Infinity, (attendee.selectedOptions[opt.id] || 0) + 1)"
                      >
                        <UIcon name="i-lucide-plus" class="size-3.5" />
                      </button>
                    </div>
                  </div>
                </template>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="flex items-center justify-between mt-5 pt-4 border-t border-gray-100 dark:border-gray-800">
      <button class="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-xl text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-[#1a1a1a] hover:bg-gray-200 dark:hover:bg-[#262626] transition-colors" @click="$emit('back')">
        <UIcon name="i-lucide-arrow-left" class="size-4" /> {{ t('commun.actions.retour') }}
      </button>
      <!-- JAMAIS désactivé : le clic valide et explique ce qui bloque. -->
      <button data-testid="btn-step2-continue" class="px-8 py-3 accent-btn rounded-xl text-sm font-semibold transition-colors" @click="auClicContinuer">
        {{ t('commun.actions.continuer') }}
      </button>
    </div>
  </div>
</template>

<style scoped>
.accent-btn {
  background: var(--accent, #212121);
  color: var(--accent-text, #fff);
}
.accent-btn:hover {
  opacity: 0.9;
}
.accent-switch :deep(button[data-state="checked"]) {
  background-color: var(--accent, #10b981) !important;
}
</style>
